import { TrackEvent } from "@api/apiClient";

interface TrackEventExtended extends TrackEvent {
  typeId?: string;
}

export interface TrackEventCore {
  id: string;
  moment: Date;
  type: number;
  cancellationReasonType?: string;
}

export interface EventInfo {
  id: string;
  childIds?: number[];
  ids?: number[];
  errorIds?: number[];
  conditionalErrorIds?: number[];
  errorsConditionMinId?: number;
  errorsConditionMaxId?: number;
}

export const getExtendedType = (event: TrackEventCore) => {
  if (event.cancellationReasonType && event.type === 5000) return +`5000${ProblemReasonType[event.cancellationReasonType ?? ""]}`;
  return event.type;
}

export const ProblemReasonType: Record<string, number> = {
  Lost: 1,
  NotPickedUpAtPickupPoint: 2,
  ContainsProhibitedItem: 3,
  UndeliveredBySeller: 4,
  MarketplaceCancellation: 5,
  BuyerRejection: 6,
  LastMileNotApproved: 7,
  SellerRejection: 8,
  Oversized: 9,
  ProhibitedByInternalCustoms: 10,
  Reexport: 11,
  ProhibitedByCustomsIncorrectPassportData: 12,
  LostFirstMile: 13,
  SellerBanned: 14,
  LostAtUnknownStage: 15,
  LostLastMile: 16,
  LostConsolidator: 17,
  CustomsTerminationOther: 18,
  CustomsTerminationNoValidPassportData: 19,
  CustomsTerminationNoRequiredDocuments: 20,
  CustomsTerminationCommercialUse: 21,
  CustomsTerminationUnpaidCustomsDuty: 22,
  OverWeight: 23,
  InvalidFirstMile: 24,
  LostByCustomsBroker: 25,
  PackingDoesNotMeetRequirements: 26,
  QualityControl: 27,
  HazardMaterials: 29,
  ProhibitedByExportCustoms: 30,
  LabelDoesNotMeetRequirements: 31,
  ParcelDamaged: 32,
  CustomsTerminationNotPersonalUse: 33,
  SideReasons: 34,
  BuyerRejectedCustomsDuty: 35,
  CustomsHoldExceeded: 38,
  DangerousGoods: 41,
  UnderWeight: 50,
}

// Legacy-коды обратного потока (20000–21500) → ReturnFlow-статусы (30000–31500): таймлайны,
// прогресс и фильтры работают на новых кодах, старые приводим к их ReturnFlow-аналогам.
// После окончательного отказа от legacy-кодов этот маппинг можно просто удалить.
export const legacyToReturnFlowType: Record<number, number> = {
  20000: 30000,
  // склад возврата
  20001: 30101, 20002: 30102, 20003: 30103, 20004: 30104,
  20020: 30120, 20021: 30121, 20022: 30122, 20023: 30123, 20025: 30125,
  20035: 30035, 20045: 30145, 20050: 30150,
  // курьерская служба
  20051: 30051, 20052: 30052, 20054: 30054, 20055: 30055,
  // таможня экспорт
  20101: 30201, 20150: 30250,
  // таможня импорт
  20201: 30301, 20250: 30350, 20300: 30300, 20303: 30301, 20304: 30304,
  20306: 30306, 20321: 30321, 20340: 30340, 20360: 30350, 20361: 30351,
  // СЦ в Китае
  20402: 30402, 20450: 30450, 20451: 30451,
  // склад последней мили
  20301: 30501, 20302: 30502, 20305: 30505, 20309: 30509, 20310: 30510,
  20320: 30520, 20350: 30550,
  // терминальные
  21000: 31000, 21450: 31450, 21500: 31500,
};

export const normalizeTrackEventType = (type: number): number => legacyToReturnFlowType[type] ?? type;

const isConditionalErrorsAvailable = (event: EventInfo, lastNoErrorEventId?: string) => {
  if (!lastNoErrorEventId || !event.conditionalErrorIds) return false;
  if (event.errorsConditionMinId && +lastNoErrorEventId < event.errorsConditionMinId) return false;
  if (event.errorsConditionMaxId && +lastNoErrorEventId > event.errorsConditionMaxId) return false;
  return true;
}

export const getErrorIds = (event: EventInfo, lastNoErrorEventId?: string) => {
  return [
    ...(event.errorIds ?? []),
    ...(isConditionalErrorsAvailable(event, lastNoErrorEventId) ? (event.conditionalErrorIds ?? []) : []),
  ]
}

export const events: EventInfo[] = [
  { id: "0", childIds: [0, 99] },
  {
    id: "101",
    childIds: [100, 149],
  },
  {
    id: "150",
    childIds: [151, 249],
    errorIds: [50005, 50009, 500023, 500027, 500029, 500031, 500032, 500041],
    conditionalErrorIds: [5000, 50003, 500026],
    errorsConditionMaxId: 199,
  },
  { id: "201" },
  {
    id: "250",
    childIds: [251, 251],
    errorIds: [500030],
    conditionalErrorIds: [5000, 50003, 500010, 500011, 500012, 500018, 500019, 500020, 500021, 500022, 500026, 500033, 500035, 500038],
    errorsConditionMinId: 200,
    errorsConditionMaxId: 249,
  },
  { id: "301", childIds: [300, 349] },
  {
    id: "350",
    childIds: [351, 351],
    errorIds: [],
    conditionalErrorIds: [5000, 50003, 500010, 500011, 500012, 500018, 500019, 500020, 500021, 500022, 500026, 500033, 500035, 500038],
    errorsConditionMinId: 250,
  },
  { id: "401", childIds: [400, 449] },
  { id: "450", childIds: [450, 499] },
  { id: "404" },
  {
    id: "10000",
    ids: [10000, 10001, 50002, 50006],
    errorIds: [50002, 50006],
  },
];

export const eventsAll = [
  [0, 99],
  [100, 149],
  [150, 199],
  [200, 249],
  [250, 299],
  [300, 349],
  [350, 399],
  [400, 403],
  [404, 550],
  [5000, 10001],
];

export const returnEventsAll = [
  [6000, 30145],
  [30150, 30151],
  [30200, 30251],
  [30300, 30351],
  [30400, 30656],
  [31000, 31500],
];

// В ids узлов кроме кода «принято» (x01) указан и код «прибыло» (x00): этап должен подсвечиваться
// уже по прибытию, а не только по приёмке. Порядок важен — первым ищется более поздний код.
export const returnEvents = [
  { id: "6000", ids: [6000, 6001] },
  { id: "30000", childIds: [30001, 30099], },
  { id: "30101", ids: [30101, 30100], childIds: [30100, 30150], },
  { id: "30150" },
  { id: "30201", ids: [30201, 30200], childIds: [30200, 30250], },
  { id: "30250" },
  { id: "30301", ids: [30301, 30300], childIds: [30300, 30350], },
  { id: "30350" },
  { id: "30501", ids: [30501, 30500], childIds: [30400, 30509], },
  { id: "30550", ids: [30550, 30505], errorIds: [30505], childIds: [30510, 30656] },
  { id: "31000", ids: [31000, 31500], errorIds: [31500] },
]

export const returnProblematicEvents = [
  { id: "6000", ids: [6000, 6001] },
  { id: "30000" },
  { id: "30501", ids: [30501, 30500], childIds: [30001, 30509], },
  { id: "30550", ids: [30550, 30505], errorIds: [30505], childIds: [30510, 30656] },
  { id: "31000", ids: [31000, 31500], errorIds: [31500] },
]

export const returnProblematicEventsRu = [
  { id: "30000" },
  { id: "30501", ids: [30501, 30500], childIds: [30001, 30500] },
  { id: "31000", ids: [31000, 31500, 31450], errorIds: [31500] },
]

export const returnEventsRu = [
  { id: "30000", childIds: [30001, 30099], },
  { id: "30101", ids: [30101, 30100], childIds: [30100, 30150], },
  { id: "30150" },
  { id: "30501", ids: [30501, 30500] },
  { id: "31000", ids: [31000, 31500, 31450], errorIds: [31500] },
]

export const reexportEvents = [
  { id: "70", childIds: [71, 79], },
  { id: "80" },
  { id: "90", childIds: [91, 99], },
  { id: "100" },
  { id: "110", childIds: [111, 139], },
  { id: "140", childIds: [141, 199], },
  { id: "150", ids: [150, 160], errorIds: [160, 210, 270] },
]

export const reexportEventsNotUNI = [
  { id: "0" },
  { id: "110", childIds: [110, 140], },
  { id: "140", childIds: [141, 199], },
  { id: "150", ids: [150, 160], errorIds: [160, 210, 270] },
]

export const getLastTrackEvent = (trackEvents: TrackEvent[]): TrackEventExtended | null => {
  if (!trackEvents?.length) return null;
  for (let idx = events.length - 1; idx >= 0; idx--) {
    const res = trackEvents.find(v => (
      (String(v.typeCode) === events[idx].id)
      || (events[idx].ids || []).includes(v.typeCode)
    ));
    if (res) return {
      ...res,
      typeId: String(res.typeCode),
    };
  }
  return null;
}

export const getProgressByTrackEvents = (trackEvents: TrackEvent[]): number => {
  const event = getLastTrackEvent(trackEvents);
  if (!event) return 0;
  const n = events.findIndex(v => (
    (v.id === String(event.typeCode))
    || (v.ids || []).includes(event.typeCode)
  )) + 1;
  return Math.round(100 * n / events.length);
}

export const getProgressByTrackEventCode = (idRaw?: number): number => {
  if (!idRaw) return 0;
  const id = normalizeTrackEventType(idRaw);
  const curEvents = (id === 6000 || id === 6001 || id >= 20000) ? returnEventsAll : eventsAll;
  const n = curEvents.findIndex(v => (
    (v[0] <= id) && (v[1] >= id)
  )) + 1;
  return Math.round(100 * n / curEvents.length);
}

export const getProgressByTrackEventFilterValue = (name?: string): number => {
  const values: Record<string, number> = {
    LastMileAccepted: 75,
    FirstMileAccepted: 50,
    ParcelAccepted: 25,
    Delivered: 100,
  }
  return values[name ?? ""] || 0;
}
